import { useEffect, useState, type ImgHTMLAttributes } from 'react';
import { useStorageUrl } from '../hooks/use-storage-url';

interface ChannelMediaImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    value: string | null | undefined;
    fallbackValue?: string | null;
}

function isLocalPreview(value: string | null | undefined) {
    return Boolean(value && /^(blob:|data:)/i.test(value));
}

export function ChannelMediaImage({
    value,
    fallbackValue,
    onError,
    ...props
}: ChannelMediaImageProps) {
    const localPreview = isLocalPreview(value);
    const { url } = useStorageUrl(localPreview ? null : value, 'channel-media');
    const { url: fallbackUrl } = useStorageUrl(
        isLocalPreview(fallbackValue) ? null : fallbackValue,
        'channel-media',
    );
    const [showFallback, setShowFallback] = useState(false);

    useEffect(() => {
        setShowFallback(false);
    }, [value, fallbackValue]);

    const primaryUrl = localPreview ? value : url;
    const resolvedFallback = isLocalPreview(fallbackValue) ? fallbackValue : fallbackUrl;
    const src = showFallback ? resolvedFallback : primaryUrl;

    return (
        <img
            {...props}
            src={src ?? undefined}
            onError={(event) => {
                if (!showFallback && resolvedFallback && resolvedFallback !== primaryUrl) {
                    setShowFallback(true);
                    return;
                }
                onError?.(event);
            }}
        />
    );
}
